import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useAdvances, usePayAdvance, useSettleAdvance, useSettlementQueue } from "../../lib/api";
import { Money, SectionCard, Stat } from "../labour/shared";

const STATE_CLS: Record<string, string> = {
  Open: "bg-amber-50 text-status-amber",
  Claimed: "bg-green-50 text-status-green",
  Returned: "bg-blue-50 text-brand-primary",
  Unpaid: "bg-brand-grey-light text-brand-grey",
};

/** Floats handed out, and what came back (HRX-05).
 *
 * `Employee Advance` had never been used — and the reason turned out to be in the ledger rather than in
 * anybody's habits. The per-company `Employee Advances` accounts were created under Loans and Advances
 * (Assets), which is right, but typed **Payable**; `EmployeeAdvance.validate_advance_account_type` requires
 * **Receivable** and throws, so every attempt to raise one failed at validation. The account was prepared and
 * then made unusable by its own type.
 *
 * `Unaccounted` is the column that matters: money handed over that has neither been seen spent nor come back.
 * An advance that never closes is indistinguishable from a loan nobody agreed to. */
export default function Advances({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useAdvances(company || undefined);
  const pay = usePayAdvance();
  const queue = useSettlementQueue(company || undefined);
  const settle = useSettleAdvance();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-expenses-advances>
      <p className="max-w-4xl text-sm text-brand-grey">{t("exp.advHint")}</p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-expenses-adv-strip>
        <Stat label={t("exp.advTotal")} value={d.count} hint={t("exp.advTotalHint")} />
        <Stat label={t("exp.unaccounted")} value={<Money value={d.unaccounted} lng={lng} />}
          cls={d.unaccounted ? "text-status-amber" : "text-status-green"} hint={t("exp.unaccountedHint")} />
        <Stat label={t("exp.awaitingPayment")} value={<Money value={d.awaiting_payment} lng={lng} />}
          cls={d.awaiting_payment ? "text-brand-primary" : ""} hint={t("exp.awaitingHint")} />
        <Stat label={t("exp.openFloats")} value={d.rows.filter((r) => r.state === "Open").length}
          hint={t("exp.openFloatsHint")} />
      </div>

      <SectionCard title={t("exp.advRegister")} aside={t("exp.advN", { n: d.count })}>
        {d.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-expenses-adv-empty>{t("exp.advEmpty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("wfa.person")}</th>
                  <th className="px-4 py-2">{t("exp.purpose")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.advAmount")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.paidOut")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.spent")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.returned")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.unaccounted")}</th>
                  <th className="px-4 py-2">{t("ops.status")}</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.name} className="border-t border-brand-grey-light" data-expenses-advance={r.name}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="text-[11px] text-brand-grey">{r.company} · {r.posting_date}</div>
                    </td>
                    <td className="max-w-xs px-4 py-2 text-[12px] text-brand-grey">{r.purpose}</td>
                    <td className="px-2 py-2 text-right tabular-nums"><Money value={r.advance_amount} lng={lng} /></td>
                    <td className="px-2 py-2 text-right tabular-nums"><Money value={r.paid} lng={lng} /></td>
                    <td className="px-2 py-2 text-right tabular-nums text-brand-grey"><Money value={r.claimed} lng={lng} /></td>
                    <td className="px-2 py-2 text-right tabular-nums text-brand-grey"><Money value={r.returned} lng={lng} /></td>
                    <td className={`px-2 py-2 text-right font-semibold tabular-nums ${r.unaccounted ? "text-status-amber" : ""}`}>
                      <Money value={r.unaccounted} lng={lng} />
                    </td>
                    <td className="px-4 py-2">
                      <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATE_CLS[r.state] ?? ""}`}>
                        {t(`exp.advState.${r.state}`)}
                      </span>
                      {r.awaiting_payment > 0 && d.can_pay && (
                        <button onClick={() => pay.mutate({ advance: r.name })} disabled={pay.isPending}
                          data-expenses-pay-advance={r.name}
                          className="ml-2 rounded bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">
                          {t("exp.handOver")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>


      <SectionCard title={t("exp.settleTitle")}
        aside={queue.data ? t("exp.settleN", { n: queue.data.count, drafts: queue.data.drafts }) : undefined}>
        <p className="border-b border-brand-grey-light px-4 py-3 text-[12px] text-brand-grey">{t("exp.settleHint")}</p>
        {!queue.data ? (
          <div className="p-4"><Skeleton className="h-16" /></div>
        ) : queue.data.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-expenses-settle-empty>
            {t("exp.settleNone", { drafts: queue.data.drafts, holders: queue.data.advance_holders })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("wfa.person")}</th>
                  <th className="px-4 py-2">{t("exp.settleAdvance")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.advAmount")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.settleCovers")}</th>
                  <th className="px-2 py-2 text-right">{t("exp.settleCash")}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {queue.data.rows.map((r) => (
                  <tr key={r.claim} className="border-t border-brand-grey-light" data-expenses-settle={r.claim}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="text-[11px] text-brand-grey">{r.claim} · {r.company} · {r.posting_date}</div>
                    </td>
                    <td className="px-4 py-2 text-[12px]">
                      {r.candidates.filter((c) => c.would_settle > 0).map((c) => (
                        <div key={c.advance}>
                          {c.advance}
                          {c.advance_left > 0 && (
                            <span className="ml-1 text-[11px] text-brand-grey">
                              (<Money value={c.advance_left} lng={lng} /> {t("exp.settleLeft")})
                            </span>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums"><Money value={r.amount} lng={lng} /></td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-status-green">
                      <Money value={r.covered} lng={lng} />
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${r.cash_due ? "text-status-amber" : "text-brand-grey"}`}>
                      <Money value={r.cash_due} lng={lng} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      {queue.data.can_settle && r.candidates[0] && (
                        <button
                          onClick={() => settle.mutate({ claim: r.claim, advance: r.candidates[0].advance })}
                          disabled={settle.isPending} data-expenses-settle-do={r.claim}
                          className="rounded bg-brand-primary px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50">
                          {t("exp.settleDo")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {pay.isError && <ErrorBox error={pay.error} />}
      {settle.isError && <ErrorBox error={settle.error} />}
    </div>
  );
}
